import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { NanaeHeader, NanaeFooter } from "@/components/qv3";
import { ExpertCard } from "@/components/qorum/ExpertCard";
import { fetchCatalogue, EXPERT_CATEGORIES } from "@/lib/experts";
import { socialMeta } from "@/lib/seo-meta";

export const Route = createFileRoute("/experts/catalogue")({
  head: () => ({
    meta: [
      { title: "Le Catalogue Hōjin" },
      {
        name: "description",
        content:
          "20 experts spécialisés prêts à siéger dans votre Conseil.",
      },
      ...socialMeta({
        title: "Le Catalogue Hōjin",
        description: "20 experts spécialisés prêts à siéger dans votre Conseil.",
        url: "https://hojin.ai/experts/catalogue",
        image: "conseil",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/experts/catalogue" }],
  }),
  component: CataloguePage,
});

function CataloguePage() {
  return (
    <ProtectedRoute>
      <div className="nanae-page qv3-dark">
        <NanaeHeader variant="app" title="Catalogue" />
        <CatalogueContent />
        <NanaeFooter variant="minimal" />
      </div>
    </ProtectedRoute>
  );
}

export function CatalogueContent() {
  const [filter, setFilter] = useState<string>("all");
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["experts", "catalogue"],
    queryFn: fetchCatalogue,
    staleTime: 5 * 60_000,
  });

  const experts = data ?? [];
  const filtered = useMemo(
    () => (filter === "all" ? experts : experts.filter((e) => e.category === filter)),
    [experts, filter],
  );

  return (
    <main
      style={{
        background: "var(--paper)",
        minHeight: "100vh",
        padding: "48px 24px 80px",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <header style={{ marginBottom: 32 }}>
          <div
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: 3,
              color: "var(--terracotta)",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            ─ Vol. I — N°01 — Édition 2026
          </div>
          <h1
            style={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 900,
              fontSize: "clamp(36px, 6vw, 56px)",
              color: "var(--charcoal-deep)",
              margin: "0 0 8px",
            }}
          >
            Le Catalogue Hōjin
          </h1>
          <p
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              fontSize: 20,
              color: "var(--smoke)",
              margin: 0,
            }}
          >
            20 spécialistes prêts à siéger dans votre Conseil.
          </p>
        </header>

        <nav
          aria-label="Filtres par catégorie"
          role="tablist"
          style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}
        >
          {EXPERT_CATEGORIES.map((c) => {
            const active = filter === c.value;
            return (
              <button
                key={c.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(c.value)}
                data-testid={`catalogue-filter-${c.value}`}
                style={{
                  fontFamily: '"Oswald", sans-serif',
                  fontSize: 12,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  padding: "8px 16px",
                  border: "0.5px solid var(--charcoal)",
                  background: active ? "var(--charcoal-deep)" : "transparent",
                  color: active ? "var(--ivory)" : "var(--charcoal)",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                {c.label}
              </button>
            );
          })}
        </nav>

        {isLoading ? (
          <div
            data-testid="catalogue-loading"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))",
              gap: 16,
            }}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                aria-hidden
                style={{
                  height: 180,
                  background: "var(--cream)",
                  border: "0.5px solid var(--stone)",
                  borderRadius: 8,
                  animation: "pulse 1.4s ease-in-out infinite",
                }}
              />
            ))}
          </div>
        ) : isError ? (
          <div
            role="alert"
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 13,
              color: "var(--leather)",
              padding: 24,
              border: "0.5px solid var(--leather)",
              background: "var(--ivory)",
            }}
          >
            Erreur — {error instanceof Error ? error.message : "Catalogue indisponible"}
          </div>
        ) : filtered.length === 0 ? (
          <p style={{ fontFamily: '"DM Sans", sans-serif', color: "var(--smoke)" }}>
            Aucun expert dans cette catégorie.
          </p>
        ) : (
          <div
            data-testid="catalogue-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))",
              gap: 16,
            }}
          >
            {filtered.map((e) => (
              <ExpertCard key={e.slug} expert={e} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
